import type { ComponentProps, HTMLAttributes } from "react";
import { Button } from "@exiftrator/shared/components/ui";

type OpticButtonVariant = "primary" | "ghost" | "subtle";

type OpticButtonProps = Omit<ComponentProps<typeof Button>, "variant"> & {
  variant?: OpticButtonVariant;
};

export const GlassPanel = ({
  children,
  className = "",
  ...props
}: HTMLAttributes<HTMLDivElement>) => (
  <div
    className={`rounded-2xl border border-[#1f2430]/25 bg-white/70 shadow-[0_10px_35px_rgba(15,23,42,0.06)] backdrop-blur-xl dark:border-white/12 dark:bg-white/[0.04] dark:shadow-[inset_0_1px_0_0_rgba(255,255,255,0.08)] ${className}`}
    {...props}
  >
    {children}
  </div>
);

export const OpticButton = ({
  children,
  className = "",
  variant = "primary",
  ...props
}: OpticButtonProps) => {
  const baseStyled =
    "inline-flex h-11 items-center justify-center rounded-xl px-5 font-mono text-[11px] font-semibold uppercase tracking-[0.14em] transition-all duration-200 focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-transparent";

  const variants: Record<OpticButtonVariant, string> = {
    primary:
      "bg-[#0f172a] text-white hover:bg-[#020617] dark:bg-white dark:text-[#050505] dark:hover:bg-[#e5e7eb]",
    ghost:
      "border border-[#1f2430]/20 bg-transparent text-[#0f172a] hover:bg-[#0f172a]/6 dark:border-white/15 dark:text-white dark:hover:bg-white/10",
    subtle:
      "bg-[#0f172a]/8 text-[#0f172a] hover:bg-[#0f172a]/12 dark:bg-white/10 dark:text-white dark:hover:bg-white/14",
  };

  return (
    <Button className={`${baseStyled} ${variants[variant]} ${className}`} {...props}>
      {children}
    </Button>
  );
};

export const OpticSection = ({
  children,
  className = "",
  ...props
}: HTMLAttributes<HTMLElement>) => (
  <section className={`mx-auto w-full max-w-[1200px] px-6 ${className}`} {...props}>
    {children}
  </section>
);

export const OpticEyebrow = ({
  children,
  className = "",
  ...props
}: HTMLAttributes<HTMLParagraphElement>) => (
  <p
    className={`font-mono text-[11px] font-medium uppercase tracking-[0.18em] text-[#0f172a]/65 dark:text-[#94a3b8] ${className}`}
    {...props}
  >
    {children}
  </p>
);

export const OpticHeading = ({
  children,
  className = "",
  ...props
}: HTMLAttributes<HTMLHeadingElement>) => (
  <h2 className={`text-3xl tracking-tight text-[#0f172a] dark:text-white sm:text-4xl ${className}`} {...props}>
    {children}
  </h2>
);

export const OpticBody = ({
  children,
  className = "",
  ...props
}: HTMLAttributes<HTMLParagraphElement>) => (
  <p className={`text-sm leading-relaxed text-[#334155] dark:text-[#94a3b8] sm:text-base ${className}`} {...props}>
    {children}
  </p>
);
